import React from "react";
import classes from "../style.module.scss";

export default () => (
    <>
        <div className={classes.faq}>
            <p>
                <strong>Last updated: 2026-03-30</strong>
            </p>
            <p>
                Volta SNAP is an energy modelling platform for residential buildings that provides insights into energy
                performance (the "Services") for industry professionals ("End Users"). It is provided by Volta Research
                Inc. ("we", "us," or "our"), located at 1655 Dupont St, Toronto, ON.
            </p>
            <p>
                You may engage with our Services through the Volta SNAP web{" "}
                <a rel="noopener noreferrer" target="_blank" href="https://app.voltasnap.ca/login">
                    application
                </a>
                .
            </p>
            <p>
                If you are a visitor to our{" "}
                <a rel="noopener noreferrer" target="_blank" href="https://voltaresearch.org/">
                    website
                </a>
                , please refer to our Website Privacy Policy at{" "}
                <a rel="noopener noreferrer" target="_blank" href="https://voltaresearch.org/privacy-policy">
                    voltaresearch.org/privacy-policy
                </a>
                . This Privacy Policy does not apply to your use of our website.
            </p>
            <p>
                This Privacy Policy describes how we collect, store, use, and disclose the Personal Data (as defined
                below) about End-Users ("you") of Volta SNAP. It is primarily governed by Canadian privacy laws,
                including the Personal Information Protection and Electronic Documents Act ("PIPEDA"), and applicable
                provincial privacy legislation.
            </p>
            <p>
                While we do not actively market or direct our Services to individuals outside of Canada, we endeavor to
                align our privacy practices with the principles of the EU General Data Protection Regulation ("GDPR")
                where relevant and appropriate.
            </p>
        </div>
        <div className={classes.faq}>
            <h3>1. Personal Data Collection</h3>
            <p>
                "Personal Data" means any information that identifies, relates to, describes, is reasonably capable of
                being associated with, or could reasonably be linked, directly or indirectly, to an individual. It does
                not include aggregated or deidentified information that is maintained in a form that is not reasonably
                capable of being associated with or linked to an individual.
            </p>
            <p>
                The list below describes the categories of Personal Data that we process about End-Users through our
                Services:
            </p>
            <ul>
                <li>
                    <strong>Account Data:</strong> We collect first and last name, company name, email address, phone
                    number, and other basic contact details, as well as any other Personal Data you choose to provide
                    when you create a Volta SNAP account.
                </li>
                <li>
                    <strong>User Data:</strong> When you use Volta SNAP, we may collect additional Personal Data about
                    you, including that contained within your energy assessment file. We may also generate such data
                    with the help of third-party services (as detailed in Section 4 below). Such data mainly consists of
                    connectivity, technical and aggregated information, such as your IP address, general location, date
                    and time stamp.
                </li>
            </ul>
        </div>
        <div className={classes.faq}>
            <h3>2. Purposes of Data Processing</h3>
            <p>
                We use your Personal Data for the following purposes, in reliance on the lawful bases for processing
                noted next to them:
            </p>
            <ul>
                <li>
                    <strong>To track and respond to bugs and other technical problems with our Services</strong> —
                    Lawful Basis: Legitimate interest (improving our Services)
                </li>
                <li>
                    <strong>
                        To create aggregated statistical data, inferred non-personal data, or anonymized or
                        pseudonymized data
                    </strong>
                    , which we use to generate default costing data statistics that other users across the application
                    can leverage — Lawful Basis: Legitimate interest (improving our Services, improving user experience)
                </li>
            </ul>
        </div>
        <div className={classes.faq}>
            <h3>3. Data Retention and Storage</h3>
            <p>
                We retain Personal Data for as long as is required to comply with our legal and contractual obligations,
                and to protect ourselves from any potential disputes (i.e. as required by laws applicable to
                log-keeping, records and bookkeeping, and in order to have proof and evidence concerning our
                relationship, should any legal issues arise following your discontinuance of use).
            </p>
            <p>
                Please note that except as required by applicable law, we will not be obligated to retain your Personal
                Data for any period, and we are free to securely delete it for any reason and at any time, with or
                without notice to you. If you have any questions, please contact us at info@voltaresearch.org.
            </p>
        </div>
        <div className={classes.faq}>
            <h3>4. Data Disclosure & Transfers</h3>
            <p>
                <strong>Legal Compliance:</strong> We may disclose or allow government and law enforcement officials
                access to your Personal Data in response to a subpoena, search warrant or court order (or similar
                requirement), or in compliance with applicable laws and regulations. Such disclosure or access may occur
                if we believe in good faith that we are legally compelled to do so, or that disclosure is appropriate in
                connection with efforts to investigate, prevent, or act regarding actual or suspected illegal activity,
                fraud, or other wrongdoing.
            </p>
            <p>
                <strong>Service Providers:</strong> We may engage selected third-party companies and individuals to
                perform services complementary to our own (e.g. hosting services, data analytics services, user
                engagement services, and our business, legal and financial advisors, government, academic, and industry
                research groups) (collectively, "Service Providers"). These Service Providers may have access to your
                Personal Data, depending on each of their specific roles and purposes in facilitating and enhancing our
                Services, and may only use it for such purposes.
            </p>
            <p>
                <strong>International Transfers:</strong> Some of our Service Providers may be located outside of
                Canada, including in the United States. When we transfer your Personal Data outside of Canada, it will
                be subject to the laws of the jurisdiction in which it is stored or processed, and may be accessible to
                law enforcement and national security authorities in those jurisdictions.
            </p>
        </div>
        <div className={classes.faq}>
            <h3>5. Data Subject Rights</h3>
            <p>
                Individuals have rights concerning their Personal Data. Please contact us by email at
                info@voltaresearch.org if you wish to exercise your privacy rights under any applicable law, including
                the GDPR, such as — to the extent applicable — the right to request access to your Personal Data held
                with us, rectification or erasure of your Personal Data, to restrict or object to such Personal Data's
                processing, to port such Personal Data, or to withdraw your consent to our use of it.
            </p>
            <p>
                Please note that when you ask us to exercise any of your rights under this Privacy Policy or applicable
                law, we may need to ask you to provide us certain credentials to make sure that you are who you claim
                you are, to avoid disclosure to you of personal data related to others and to ask you to provide further
                information to better understand the nature and scope of data that you request to access. Such
                additional data will be then retained by us for legal purposes (e.g., as proof of the identity of the
                person submitting the request). If you are a GDPR-protected individual, you also have the right to lodge
                a complaint with the relevant supervisory authority.
            </p>
        </div>
        <div className={classes.faq}>
            <h3>6. Communications & Updates</h3>
            <p>
                We may update and amend this Privacy Policy from time to time by publishing an amended version,
                effective as of the date it is published. We will provide a 10-day prior notice if we believe any
                substantial changes are involved via the communication means available to us. After such notice period,
                all amendments shall be deemed accepted by you.
            </p>
        </div>
    </>
);
